import React, { useMemo } from 'react';
import 'react-table/react-table.css';
import DefaultReactTable, { multiSelectFilterColumn } from '../../../utils/react-table';
import { Dropdown } from '../../ui-kit';
import { IconCog, IconFileExcel, IconFileCSV, IconFilter } from '../../ui-kit/icons';
import { sortBy } from 'lodash';

const Toolbar = ({ handleShowFilters, exportToCSV, exportToXLSX }) => (
  <Dropdown align="end">
    <Dropdown.Toggle variant="link" bsPrefix="none">
      <IconCog />
    </Dropdown.Toggle>
    <Dropdown.Menu>
      <Dropdown.Item onClick={handleShowFilters}>
        <IconFilter fixedWidth /> {I18n.t('reports.select_columns')}
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item onClick={exportToCSV}>
        <IconFileCSV fixedWidth /> {I18n.t('reports.download_as_csv')}
      </Dropdown.Item>
      <Dropdown.Item onClick={exportToXLSX}>
        <IconFileExcel fixedWidth /> {I18n.t('reports.download_as_xlsx')}
      </Dropdown.Item>
    </Dropdown.Menu>
  </Dropdown>
);

const createColumn = (column, data) => {
  const isEnum = column.enum || column.type === 'boolean';
  return isEnum ? filterableColumn(column, data) : generalColumn(column);
};

const generalColumn = column => ({
  Header: column.i18n_attribute,
  id: column.attribute,
  accessor: d => d[column.attribute].value,
  filterable: column.filterable,
  sortable: column.sortable,
  width: column.width,
  Cell: row => {
    let className = 'general-report-cell';
    if (column.align != 'start') className += ` d-flex justify-content-${column.align}`;
    const value = row.original[column.attribute].formatted_value;

    return <div className={className} dangerouslySetInnerHTML={{ __html: value }} />;
  }
});

const uniqueValues = (data, column) => {
  const { attribute, type } = column;

  if (type === 'boolean') {
    return [
      { value: true, html: I18n.t('yes_word') },
      { value: false, html: I18n.t('no_word') }
    ];
  }

  let result = [];
  for (const d of data) {
    const value = d[attribute].value;
    if (value === undefined || value === null) continue;

    result[value] = result[value] || {
      value,
      html: <div dangerouslySetInnerHTML={{ __html: d[attribute].formatted_value }} />
    };
  }

  return sortBy(Object.values(result), 'value');
};

const filterableColumn = (column, data) =>
  multiSelectFilterColumn({
    ...generalColumn(column),
    selectOptions: uniqueValues(data, column),
    includeEmpty: column.type !== 'boolean'
  });

const TableReport = ({ columnsByKey, data, columnKeys, handleShowFilters, exportToCSV, exportToXLSX }) => {
  data = data || [];
  columnKeys = columnKeys || [];
  const selectedColumns = columnKeys.map(key => columnsByKey[key]).filter(Boolean);

  const columns = useMemo(
    () => [
      ...selectedColumns.map(column => createColumn(column, data)),
      {
        Header: <Toolbar handleShowFilters={handleShowFilters} exportToCSV={exportToCSV} exportToXLSX={exportToXLSX} />,
        width: 40,
        filterable: false,
        sortable: false,
        resizable: false
      }
    ],
    [selectedColumns, data, handleShowFilters, exportToXLSX, exportToCSV]
  );

  return <DefaultReactTable filterable data={data} columns={columns} />;
};

export default TableReport;
